import { type JSX, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Psychology,
  ExpandMore,
  Code as CodeIcon,
  LocalHospital,
  Warning,
  CheckCircle,
  Person,
  MedicalServices,
  Assessment,
  Psychology as PsychologyIcon,
  CalendarToday,
  Place,
  MenuOpen,
  Close,
} from '@mui/icons-material';

import { CardTitle } from './card-title';
import { JsonViewerModal } from './json-viewer-modal';
import type { EncounterAnalysis } from '../types';
import { formatDateTime } from '../utils/formatting';

interface EncounterAnalysisCardProps {
  analysis: EncounterAnalysis;
}

// Helper to get status color
const getStatusColor = (
  status: string,
): 'success' | 'warning' | 'default' | 'error' | 'primary' | 'secondary' | 'info' => {
  const normalizedStatus = status?.toLowerCase() || '';
  if (normalizedStatus.includes('at_goal') || normalizedStatus.includes('resolved')) {
    return 'success';
  }
  if (normalizedStatus.includes('not_at_goal') || normalizedStatus.includes('active')) {
    return 'warning';
  }
  return 'default';
};

// Helper to get risk status color and icon
const getRiskStatusDisplay = (
  status: string,
): { color: 'success' | 'warning' | 'error' | 'default'; icon: JSX.Element } => {
  const normalizedStatus = status?.toLowerCase() || '';
  if (normalizedStatus === 'no_concerns' || normalizedStatus === 'none') {
    return { color: 'success', icon: <CheckCircle className="h-4 w-4" /> };
  }
  if (normalizedStatus === 'monitor' || normalizedStatus === 'watch') {
    return { color: 'warning', icon: <Warning className="h-4 w-4" /> };
  }
  if (normalizedStatus === 'concerns' || normalizedStatus === 'high') {
    return { color: 'error', icon: <Warning className="h-4 w-4" /> };
  }
  return { color: 'default', icon: <Assessment className="h-4 w-4" /> };
};

export function EncounterAnalysisCard({ analysis }: EncounterAnalysisCardProps) {
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedSections(new Set());
    } else {
      setExpandedSections(
        new Set([
          'summary',
          'problems',
          'treatment',
          'participants',
          'risk',
          'activities',
          'attestations',
          'clinical_markers',
          'data_reviewed',
        ]),
      );
    }
    setExpandAll(!expandAll);
  };

  const isSectionExpanded = (section: string) => expandedSections.has(section);

  if (!analysis) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader
          title={
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Psychology className="mr-2 h-5 w-5" />
                Encounter Analysis
              </div>
              <div className="flex items-center gap-2">
                {analysis.schema_version && (
                  <Chip
                    label={`v${analysis.schema_version}`}
                    size="small"
                    variant="outlined"
                    className="text-xs"
                  />
                )}
                {analysis.analyzed_at && (
                  <Chip
                    label={formatDateTime(analysis.analyzed_at)}
                    size="small"
                    variant="outlined"
                    className="text-xs"
                  />
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => setJsonModalOpen(true)}
                  className="ml-2"
                >
                  View JSON
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MenuOpen />}
                  onClick={() => setDrawerOpen(true)}
                  className="ml-2"
                >
                  View Details
                </Button>
              </div>
            </CardTitle>
          }
        />
        <CardContent>
          <div className="space-y-4">
            {/* Summary Section - Always Visible */}
            {analysis.summary && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <PsychologyIcon className="h-4 w-4 text-purple-600" />
                  <Typography variant="caption" className="text-gray-900 font-semibold">
                    Summary
                  </Typography>
                </div>
                <Box className="p-2 bg-purple-50 rounded border border-purple-100">
                  <Typography variant="body2" className="text-gray-800 text-sm leading-relaxed">
                    {analysis.summary}
                  </Typography>
                </Box>
              </div>
            )}

            {/* Key MDM Info Grid - Always Visible */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Problems Addressed - MDM Priority */}
              <Box
                className="p-3 bg-orange-50 rounded border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
                onClick={() => setDrawerOpen(true)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <LocalHospital className="h-4 w-4 text-orange-600" />
                  <Typography variant="caption" className="text-orange-900 font-medium">
                    Problems Addressed
                  </Typography>
                </div>
                <Typography variant="h5" className="text-orange-900 font-bold">
                  {analysis.problems_addressed?.length || 0}
                </Typography>
                {analysis.problems_addressed && analysis.problems_addressed.length > 0 && (
                  <Typography variant="caption" className="text-orange-700 block mt-1 line-clamp-1">
                    {analysis.problems_addressed[0].problem}
                    {analysis.problems_addressed.length > 1 &&
                      ` +${analysis.problems_addressed.length - 1} more`}
                  </Typography>
                )}
              </Box>

              {/* Risk Assessment - MDM Priority */}
              {analysis.risk_assessment && (
                <Box
                  className="p-3 bg-green-50 rounded border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => setDrawerOpen(true)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <LocalHospital className="h-4 w-4 text-green-600" />
                    <Typography variant="caption" className="text-green-900 font-medium">
                      Risk Level
                    </Typography>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRiskStatusDisplay(analysis.risk_assessment.safety_status).icon}
                    <Chip
                      label={analysis.risk_assessment.safety_status.replace(/_/g, ' ').toUpperCase()}
                      size="small"
                      color={getRiskStatusDisplay(analysis.risk_assessment.safety_status).color}
                      className="font-semibold"
                    />
                  </div>
                </Box>
              )}

              {/* Data Reviewed - MDM Priority */}
              <Box
                className="p-3 bg-purple-50 rounded border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => setDrawerOpen(true)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Assessment className="h-4 w-4 text-purple-600" />
                  <Typography variant="caption" className="text-purple-900 font-medium">
                    Data Reviewed
                  </Typography>
                </div>
                <Typography variant="h5" className="text-purple-900 font-bold">
                  {analysis.data_reviewed?.length || 0}
                </Typography>
                {analysis.data_reviewed && analysis.data_reviewed.length > 0 && (
                  <Typography variant="caption" className="text-purple-700 block mt-1 line-clamp-1">
                    {analysis.data_reviewed[0].type}
                    {analysis.data_reviewed.length > 1 && ` +${analysis.data_reviewed.length - 1} more`}
                  </Typography>
                )}
              </Box>
            </div>

            {/* Secondary Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              {/* Session Type */}
              {analysis.session_type && (
                <Box className="p-2 bg-blue-50 rounded border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <CalendarToday className="h-3 w-3 text-blue-600" />
                    <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                      Session Type
                    </Typography>
                  </div>
                  <Typography variant="body2" className="text-blue-900 font-semibold text-sm">
                    {analysis.session_type.canonical || 'Not specified'}
                  </Typography>
                </Box>
              )}

              {/* Modality & Place */}
              <Box className="p-2 bg-gray-50 rounded border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Place className="h-3 w-3 text-gray-600" />
                  <Typography variant="caption" className="text-gray-900 font-medium text-xs">
                    Setting
                  </Typography>
                </div>
                <Typography variant="body2" className="text-gray-900 font-semibold text-sm">
                  {analysis.modality || 'Not specified'}
                  {analysis.place_of_care && ` • ${analysis.place_of_care}`}
                </Typography>
              </Box>

              {/* Treatment Facts Summary */}
              {analysis.treatment_facts && (
                <Box
                  className="p-2 bg-green-50 rounded border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => setDrawerOpen(true)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <MedicalServices className="h-3 w-3 text-green-600" />
                    <Typography variant="caption" className="text-green-900 font-medium text-xs">
                      Treatment
                    </Typography>
                  </div>
                  <Typography variant="body2" className="text-green-900 font-semibold text-sm">
                    {(analysis.treatment_facts.medications?.length || 0) +
                      (analysis.treatment_facts.procedures?.length || 0) +
                      (analysis.treatment_facts.supplements?.length || 0)}{' '}
                    items
                  </Typography>
                </Box>
              )}
            </div>
          </div>

          {/* Semantic Tags and Treatment Modalities - Above the Fold */}
          <div className="space-y-3 mt-4 pt-4 border-t border-gray-200">
            {/* Semantic Tags */}
            {analysis.semantic_tags && analysis.semantic_tags.length > 0 && (
              <div>
                <Typography variant="caption" className="text-gray-600 block mb-2 font-medium">
                  Semantic Tags
                </Typography>
                <div className="flex flex-wrap gap-1">
                  {analysis.semantic_tags.map((tag, idx) => (
                    <Chip key={idx} label={tag} size="small" variant="outlined" className="text-xs" />
                  ))}
                </div>
              </div>
            )}

            {/* Treatment Modalities */}
            {analysis.treatment_modalities && analysis.treatment_modalities.length > 0 && (
              <div>
                <Typography variant="caption" className="text-gray-600 block mb-2 font-medium">
                  Treatment Modalities
                </Typography>
                <div className="flex flex-wrap gap-1">
                  {analysis.treatment_modalities.map((modality, idx) => (
                    <Chip
                      key={idx}
                      label={modality}
                      size="small"
                      color="primary"
                      variant="outlined"
                      className="text-xs"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)} slotProps={{ paper: {
          sx: { width: { xs: '100%', sm: '600px', md: '700px' }, maxWidth: '90vw' },
        } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div className="flex items-center gap-2">
              <Psychology className="h-6 w-6 text-purple-600" />
              <Typography variant="h6">Encounter Analysis Details</Typography>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="text"
                size="small"
                onClick={handleExpandAll}
              >
                {expandAll ? 'Collapse All' : 'Expand All'}
              </Button>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <Close />
              </IconButton>
            </div>
          </Box>

          {/* Drawer Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <div className="space-y-4">
              {/* Full Summary */}
              {analysis.summary && (
                <div>
                  <div
                    className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                    onClick={() => toggleSection('summary')}
                  >
                    <div className="flex items-center gap-2">
                      <PsychologyIcon className="h-5 w-5 text-purple-600" />
                      <Typography variant="subtitle2" className="font-semibold">
                        Full Summary
                      </Typography>
                    </div>
                    <IconButton size="small">
                      <ExpandMore
                        sx={{
                          transform: isSectionExpanded('summary')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </div>
                  <Collapse in={isSectionExpanded('summary')}>
                    <Box className="p-3 bg-purple-50 rounded border border-purple-200 mt-2">
                      <Typography variant="body2" className="text-gray-900">
                        {analysis.summary}
                      </Typography>
                    </Box>
                  </Collapse>
                </div>
              )}

              {/* Problems Addressed */}
              {analysis.problems_addressed && analysis.problems_addressed.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => toggleSection('problems')}
                >
                  <div className="flex items-center gap-2">
                    <LocalHospital className="h-5 w-5 text-orange-600" />
                    <Typography variant="subtitle2" className="font-semibold">
                      Problems Addressed ({analysis.problems_addressed.length})
                    </Typography>
                  </div>
                  <IconButton size="small">
                    <ExpandMore
                      sx={{
                        transform: isSectionExpanded('problems')
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                <Collapse in={isSectionExpanded('problems')}>
                  <div className="space-y-2 mt-2">
                    {analysis.problems_addressed.map((problem, idx) => (
                      <Box
                        key={idx}
                        className="p-3 bg-orange-50 rounded border border-orange-200"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <Typography variant="body2" className="font-semibold text-gray-900">
                            {problem.problem}
                          </Typography>
                          <Chip
                            label={problem.status.replace(/_/g, ' ')}
                            size="small"
                            color={getStatusColor(problem.status)}
                            className="ml-2"
                          />
                        </div>
                        {problem.details && (
                          <Typography variant="body2" className="text-gray-700 text-sm">
                            {problem.details}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </div>
                </Collapse>
              </div>
            )}

            {/* Treatment Facts */}
            {analysis.treatment_facts && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => toggleSection('treatment')}
                >
                  <div className="flex items-center gap-2">
                    <MedicalServices className="h-5 w-5 text-green-600" />
                    <Typography variant="subtitle2" className="font-semibold">
                      Treatment Facts
                    </Typography>
                    <Chip
                      label={`${(analysis.treatment_facts.medications?.length || 0) + (analysis.treatment_facts.procedures?.length || 0) + (analysis.treatment_facts.supplements?.length || 0)} items`}
                      size="small"
                      variant="outlined"
                      className="text-xs"
                    />
                  </div>
                  <IconButton size="small">
                    <ExpandMore
                      sx={{
                        transform: isSectionExpanded('treatment')
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                <Collapse in={isSectionExpanded('treatment')}>
                  <div className="space-y-3 mt-2">
                    {analysis.treatment_facts.medications &&
                      analysis.treatment_facts.medications.length > 0 && (
                        <Box className="p-3 bg-green-50 rounded border border-green-200">
                          <Typography
                            variant="caption"
                            className="text-green-900 font-semibold block mb-2"
                          >
                            Medications ({analysis.treatment_facts.medications.length})
                          </Typography>
                          <ul className="list-disc list-inside space-y-1">
                            {analysis.treatment_facts.medications.map((med, idx) => (
                              <li key={idx} className="text-sm text-gray-900">
                                {med}
                              </li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    {analysis.treatment_facts.procedures &&
                      analysis.treatment_facts.procedures.length > 0 && (
                        <Box className="p-3 bg-blue-50 rounded border border-blue-200">
                          <Typography
                            variant="caption"
                            className="text-blue-900 font-semibold block mb-2"
                          >
                            Procedures ({analysis.treatment_facts.procedures.length})
                          </Typography>
                          <ul className="list-disc list-inside space-y-1">
                            {analysis.treatment_facts.procedures.map((proc, idx) => (
                              <li key={idx} className="text-sm text-gray-900">
                                {proc}
                              </li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    {analysis.treatment_facts.supplements &&
                      analysis.treatment_facts.supplements.length > 0 && (
                        <Box className="p-3 bg-purple-50 rounded border border-purple-200">
                          <Typography
                            variant="caption"
                            className="text-purple-900 font-semibold block mb-2"
                          >
                            Supplements ({analysis.treatment_facts.supplements.length})
                          </Typography>
                          <ul className="list-disc list-inside space-y-1">
                            {analysis.treatment_facts.supplements.map((supp, idx) => (
                              <li key={idx} className="text-sm text-gray-900">
                                {supp}
                              </li>
                            ))}
                          </ul>
                        </Box>
                      )}
                  </div>
                </Collapse>
              </div>
            )}

            {/* Participants */}
            {analysis.participants && analysis.participants.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => toggleSection('participants')}
                >
                  <div className="flex items-center gap-2">
                    <Person className="h-5 w-5 text-blue-600" />
                    <Typography variant="subtitle2" className="font-semibold">
                      Participants ({analysis.participants.length})
                    </Typography>
                  </div>
                  <IconButton size="small">
                    <ExpandMore
                      sx={{
                        transform: isSectionExpanded('participants')
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                <Collapse in={isSectionExpanded('participants')}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    {analysis.participants.map((participant, idx) => (
                      <Box key={idx} className="p-3 bg-blue-50 rounded border border-blue-200">
                        <div className="flex items-center justify-between mb-1">
                          <Typography variant="body2" className="font-semibold text-gray-900">
                            {participant.role}
                          </Typography>
                          <Chip
                            label={`×${participant.count}`}
                            size="small"
                            color="primary"
                            className="text-xs"
                          />
                        </div>
                        {participant.notes && (
                          <Typography variant="caption" className="text-gray-700">
                            {participant.notes}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </div>
                </Collapse>
              </div>
            )}

            {/* Risk Assessment Details */}
            {analysis.risk_assessment?.details && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => toggleSection('risk')}
                >
                  <div className="flex items-center gap-2">
                    <Assessment className="h-5 w-5 text-green-600" />
                    <Typography variant="subtitle2" className="font-semibold">
                      Risk Assessment Details
                    </Typography>
                  </div>
                  <IconButton size="small">
                    <ExpandMore
                      sx={{
                        transform: isSectionExpanded('risk') ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                <Collapse in={isSectionExpanded('risk')}>
                  <Box className="p-3 bg-green-50 rounded border border-green-200 mt-2">
                    <Typography variant="body2" className="text-gray-900">
                      {analysis.risk_assessment.details}
                    </Typography>
                  </Box>
                </Collapse>
              </div>
            )}

            {/* Activities Performed */}
            {analysis.activities_performed && analysis.activities_performed.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => toggleSection('activities')}
                >
                  <div className="flex items-center gap-2">
                    <Assessment className="h-5 w-5 text-purple-600" />
                    <Typography variant="subtitle2" className="font-semibold">
                      Activities Performed ({analysis.activities_performed.length})
                    </Typography>
                  </div>
                  <IconButton size="small">
                    <ExpandMore
                      sx={{
                        transform: isSectionExpanded('activities')
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                <Collapse in={isSectionExpanded('activities')}>
                  <Box className="p-3 bg-purple-50 rounded border border-purple-200 mt-2">
                    <ul className="list-disc list-inside space-y-1">
                      {analysis.activities_performed.map((activity, idx) => (
                        <li key={idx} className="text-sm text-gray-900">
                          {activity}
                        </li>
                      ))}
                    </ul>
                  </Box>
                </Collapse>
              </div>
            )}

            {/* Attestations */}
            {analysis.attestations && analysis.attestations.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => toggleSection('attestations')}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <Typography variant="subtitle2" className="font-semibold">
                      Attestations ({analysis.attestations.length})
                    </Typography>
                  </div>
                  <IconButton size="small">
                    <ExpandMore
                      sx={{
                        transform: isSectionExpanded('attestations')
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                <Collapse in={isSectionExpanded('attestations')}>
                  <Box className="p-3 bg-green-50 rounded border border-green-200 mt-2">
                    <ul className="list-disc list-inside space-y-1">
                      {analysis.attestations.map((attestation, idx) => (
                        <li key={idx} className="text-sm text-gray-900">
                          {attestation}
                        </li>
                      ))}
                    </ul>
                  </Box>
                </Collapse>
              </div>
            )}

            {/* Clinical Markers */}
            {analysis.clinical_markers &&
              (analysis.clinical_markers.labs?.length ||
                analysis.clinical_markers.vitals?.length ||
                analysis.clinical_markers.scales?.length ||
                analysis.clinical_markers.device_metrics?.length) && (
                <div className="pt-4 border-t border-gray-200">
                  <div
                    className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                    onClick={() => toggleSection('clinical_markers')}
                  >
                    <div className="flex items-center gap-2">
                      <Assessment className="h-5 w-5 text-blue-600" />
                      <Typography variant="subtitle2" className="font-semibold">
                        Clinical Markers
                      </Typography>
                    </div>
                    <IconButton size="small">
                      <ExpandMore
                        sx={{
                          transform: isSectionExpanded('clinical_markers')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </div>
                  <Collapse in={isSectionExpanded('clinical_markers')}>
                    <Box className="p-3 bg-blue-50 rounded border border-blue-200 mt-2">
                      <div className="space-y-2">
                        {analysis.clinical_markers.labs && analysis.clinical_markers.labs.length > 0 && (
                          <div>
                            <Typography variant="caption" className="font-semibold text-blue-900">
                              Labs ({analysis.clinical_markers.labs.length})
                            </Typography>
                            <pre className="text-xs mt-1 overflow-x-auto">
                              {JSON.stringify(analysis.clinical_markers.labs, null, 2)}
                            </pre>
                          </div>
                        )}
                        {analysis.clinical_markers.vitals &&
                          analysis.clinical_markers.vitals.length > 0 && (
                            <div>
                              <Typography variant="caption" className="font-semibold text-blue-900">
                                Vitals ({analysis.clinical_markers.vitals.length})
                              </Typography>
                              <pre className="text-xs mt-1 overflow-x-auto">
                                {JSON.stringify(analysis.clinical_markers.vitals, null, 2)}
                              </pre>
                            </div>
                          )}
                        {analysis.clinical_markers.scales &&
                          analysis.clinical_markers.scales.length > 0 && (
                            <div>
                              <Typography variant="caption" className="font-semibold text-blue-900">
                                Scales ({analysis.clinical_markers.scales.length})
                              </Typography>
                              <pre className="text-xs mt-1 overflow-x-auto">
                                {JSON.stringify(analysis.clinical_markers.scales, null, 2)}
                              </pre>
                            </div>
                          )}
                        {analysis.clinical_markers.device_metrics &&
                          analysis.clinical_markers.device_metrics.length > 0 && (
                            <div>
                              <Typography variant="caption" className="font-semibold text-blue-900">
                                Device Metrics ({analysis.clinical_markers.device_metrics.length})
                              </Typography>
                              <pre className="text-xs mt-1 overflow-x-auto">
                                {JSON.stringify(analysis.clinical_markers.device_metrics, null, 2)}
                              </pre>
                            </div>
                          )}
                      </div>
                    </Box>
                  </Collapse>
                </div>
              )}

            {/* Data Reviewed */}
            {analysis.data_reviewed && analysis.data_reviewed.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => toggleSection('data_reviewed')}
                >
                  <div className="flex items-center gap-2">
                    <Assessment className="h-5 w-5 text-gray-600" />
                    <Typography variant="subtitle2" className="font-semibold">
                      Data Reviewed ({analysis.data_reviewed.length})
                    </Typography>
                  </div>
                  <IconButton size="small">
                    <ExpandMore
                      sx={{
                        transform: isSectionExpanded('data_reviewed')
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                <Collapse in={isSectionExpanded('data_reviewed')}>
                  <div className="space-y-2 mt-2">
                    {analysis.data_reviewed.map((item, idx) => (
                      <Box key={idx} className="p-3 bg-gray-50 rounded border border-gray-200">
                        <Typography variant="caption" className="font-semibold text-gray-900 block mb-1">
                          {item.type}
                        </Typography>
                        {item.details && (
                          <Typography variant="body2" className="text-gray-700 text-sm">
                            {item.details}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </div>
                </Collapse>
              </div>
            )}

              {/* CPT and ICD-10 Codes */}
              {((analysis.cpt_codes && analysis.cpt_codes.length > 0) ||
                (analysis.icd10_codes && analysis.icd10_codes.length > 0)) && (
                <div className="pt-4 border-t border-gray-200">
                  <Typography variant="subtitle2" className="font-semibold mb-3">
                    Billing Codes
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {analysis.cpt_codes && analysis.cpt_codes.length > 0 && (
                      <Box className="p-3 bg-blue-50 rounded border border-blue-200">
                        <Typography variant="caption" className="text-blue-900 font-semibold block mb-2">
                          CPT Codes ({analysis.cpt_codes.length})
                        </Typography>
                        <div className="flex flex-wrap gap-1">
                          {analysis.cpt_codes.map((code, idx) => (
                            <Chip
                              key={idx}
                              label={code}
                              size="small"
                              color="primary"
                              className="text-xs font-mono"
                            />
                          ))}
                        </div>
                      </Box>
                    )}
                    {analysis.icd10_codes && analysis.icd10_codes.length > 0 && (
                      <Box className="p-3 bg-purple-50 rounded border border-purple-200">
                        <Typography
                          variant="caption"
                          className="text-purple-900 font-semibold block mb-2"
                        >
                          ICD-10 Codes ({analysis.icd10_codes.length})
                        </Typography>
                        <div className="flex flex-wrap gap-1">
                          {analysis.icd10_codes.map((code, idx) => (
                            <Chip
                              key={idx}
                              label={code}
                              size="small"
                              color="secondary"
                              className="text-xs font-mono"
                            />
                          ))}
                        </div>
                      </Box>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Box>
        </Box>
      </Drawer>

      {/* JSON Viewer Modal */}
      <JsonViewerModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={analysis}
        title="Encounter Analysis - Raw JSON"
        subtitle={analysis.encounter_id || 'Full analysis data'}
      />
    </>
  );
}
